import React, { useEffect, useState, useId } from 'react';
import { Paperclip, ChevronDown } from 'lucide-react';
import backendApi from '../../services/backendApi';
import { readTicketFile, selectTicketFiles } from '../../utils/ticketAttachments';

export default function UserStoryAttachments({ taskId, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const panelId = useId();
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    let active = true;
    setFiles([]); setError(''); setPreview(null); setExpanded(false); setLoading(true);
    backendApi.client.get(`/api/userstories/${taskId}/attachments`).then(({ data }) => {
      if (active) setFiles(data.attachments || []);
    }).catch(() => { if (active) setError('Impossible de charger les pièces jointes.'); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [taskId]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);
  const upload = async event => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    const selection = selectTicketFiles(selected);
    setError(selection.error);
    if (!selection.accepted.length) return;
    setBusy(true);
    try {
      for (const file of selection.accepted) {
        const attachment = await readTicketFile(file);
        await backendApi.client.post(`/api/userstories/${taskId}/attachments`, attachment, { timeout: 120000 });
      }
    } catch (err) { setError(err.response?.data?.error || err.message || 'Échec de l’ajout.'); }
    finally {
      try { const { data } = await backendApi.client.get(`/api/userstories/${taskId}/attachments`); setFiles(data.attachments); }
      catch { setError('Impossible de recharger les pièces jointes.'); }
      setBusy(false);
    }
  };
  const open = async file => {
    setError('');
    try {
      const { data } = await backendApi.client.get(`/api/userstories/${taskId}/attachments/${file.id}`, { timeout: 120000 });
      const bytes = Uint8Array.from(atob(data.data), c => c.charCodeAt(0));
      const safeType = /^(image\/(png|jpeg|gif|webp)|application\/pdf|video\/(mp4|webm|ogg|quicktime))$/.test(data.type) ? data.type : 'application/octet-stream';
      setPreview({ name: data.name, type: safeType, url: URL.createObjectURL(new Blob([bytes], { type: safeType })) });
    } catch { setError('Impossible d’ouvrir cette pièce jointe.'); }
  };
  return <section className={compact ? 'story-attachments story-attachments--compact' : 'story-attachments pt-3'}>
    <button type="button" aria-expanded={expanded} aria-controls={panelId} onClick={() => setExpanded(value => !value)} className="story-attachments__trigger inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-500">
      {!compact && <Paperclip size={16} aria-hidden="true" />}
      <span>Pièces jointes {compact ? '· ' : '('}{loading ? '…' : error && !files.length ? '—' : files.length}{compact ? '' : ')'}</span>
      {!compact && <ChevronDown size={16} aria-hidden="true" className={expanded ? 'rotate-180' : ''} />}
    </button>
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    <div id={panelId} hidden={!expanded} className="story-attachments__panel max-h-[45vh] overflow-y-auto pr-2">
    <ul className="my-3 space-y-2">{files.map(file => <li key={file.id}><button className="text-sm text-purple-700 underline" onClick={() => open(file)}>{file.name}</button></li>)}</ul>
    {loading && <p className="text-sm text-gray-500">Chargement…</p>}
    {!loading && !error && !files.length && <p className="text-sm text-gray-500">Aucune pièce jointe</p>}
    <label className="block my-3 text-sm">{busy ? 'Ajout en cours…' : 'Ajouter des pièces jointes (Word, Excel, vidéos…)'}<input aria-label="Ajouter des pièces jointes" className="block mt-2" type="file" multiple disabled={busy} onChange={upload} /></label>
    <p className="mb-3 text-xs text-gray-500">10 Mo par document, 30 Mo par vidéo, 40 Mo par envoi (10 fichiers maximum).</p>
    {preview && <div className="mt-3">
      <div className="flex justify-between"><a href={preview.url} download={preview.name} className="text-purple-700 underline">Télécharger {preview.name}</a><button onClick={() => setPreview(null)}>Fermer l’aperçu</button></div>
      {preview.type.startsWith('image/') && <img src={preview.url} alt={preview.name} className="mt-2 max-w-full" />}
      {preview.type === 'application/pdf' && <iframe title={preview.name} src={preview.url} className="w-full h-96 mt-2" />}
      {preview.type.startsWith('video/') && <video controls src={preview.url} aria-label={preview.name} className="mt-2 max-h-96 max-w-full rounded bg-black" />}
    </div>}
    </div>
  </section>;
}
